// The tenant context the title bar switches between: which school, and which
// academic session. Every other request is scoped by these two ids.

import { apiRequest } from './api';

export interface SchoolOption {
  id: string;
  name: string;
  school_code: string;
}

/**
 * One type for the switcher and for the Sessions register.
 *
 * The dates and `is_current` were optional here while the title bar was the
 * only reader and needed nothing but `id`/`name`. They are not optional in the
 * response — v1's `AcademicSessionRecord` in `examApi.ts`, written against the
 * real server, has all five required — so the register can rely on them.
 *
 * Keeping this one type is deliberate: `DesignationOption` and
 * `DesignationRecord` were two shapes over one endpoint in v1, which meant two
 * cache entries and a designation you had just created not appearing in the
 * dropdown. Here it means a session created on the register is immediately
 * pickable in the title bar, with no reload.
 */
/**
 * The unit a session bills in. **This is the fork the whole fee UI turns on.**
 *
 * `monthly` is the K-12 behaviour and the default; `term` is for colleges that
 * bill by semester. A session speaks exactly one of them — sending months to a
 * term session, or terms to a monthly one, is refused with a 422 rather than
 * reinterpreted, so every screen that names a period has to ask first.
 */
export type BillingPeriodType = 'monthly' | 'term';

export interface AcademicSessionRecord {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  /**
   * Absent on a server predating term billing, which is read as `monthly` —
   * the behaviour every existing session already has. Never default this to
   * `term`: that would offer a semester picker on a session that will reject
   * one.
   */
  billing_period_type: BillingPeriodType;
  created_at?: string;
  updated_at?: string;
}

/** `monthly` unless the record explicitly says otherwise. See above. */
export function billingPeriodOf(
  session: { billing_period_type?: string | null } | null | undefined,
): BillingPeriodType {
  return session?.billing_period_type === 'term' ? 'term' : 'monthly';
}

/** per_page=100 so the switcher is never silently truncated. */
export async function fetchSchools(): Promise<SchoolOption[]> {
  const res = await apiRequest<SchoolOption[]>('/api/schools?per_page=100');
  return res.data;
}

/**
 * Every session in the organization, unpaged.
 *
 * The register needs the whole list rather than a page of it, and not only to
 * display: the name uniqueness check runs against it. v1 called this with no
 * `per_page` at all, so it checked the new name against whatever the server's
 * default page happened to hold — the same client-side-over-one-page mistake
 * its staff search made. A session is a per-year record; a hundred of them is
 * a century.
 */
export async function fetchAcademicSessions(): Promise<AcademicSessionRecord[]> {
  const res = await apiRequest<AcademicSessionRecord[]>('/api/academic-sessions?per_page=100');
  return res.data;
}

export interface AcademicSessionPayload {
  name: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  /**
   * Settable on create; effectively frozen afterwards. The API 422s on this key
   * once the session has raised any invoice ("This session has already raised
   * N invoice(s) as monthly…"), and again when switching `term` → `monthly`
   * while terms still exist. The form disables the control rather than letting
   * the user find that out by submitting — see `SessionForm`.
   */
  billing_period_type: BillingPeriodType;
}

export async function createAcademicSession(
  payload: AcademicSessionPayload,
): Promise<AcademicSessionRecord> {
  const res = await apiRequest<AcademicSessionRecord>('/api/academic-sessions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function updateAcademicSession(
  id: string,
  payload: Partial<AcademicSessionPayload>,
): Promise<AcademicSessionRecord> {
  const res = await apiRequest<AcademicSessionRecord>(`/api/academic-sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return res.data;
}

/*
 * There is no `deleteAcademicSession`, and the omission is deliberate.
 *
 * `DELETE /api/academic-sessions/{id}` exists — `OPTIONS` on the member route
 * answers `GET,HEAD,PUT,PATCH,DELETE` — so this is not a missing endpoint. It
 * is a verb the UI should not offer: an academic session is the scope every
 * invoice, receipt, exam, mark and attendance row for the year hangs off, and
 * deleting one from a register with a row menu is a year of records removed by
 * a mis-click. v1 offered no delete either. If a session is genuinely wrong to
 * have created, that is an administrator's job against the API directly.
 */
